import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import { useUrlFilters } from "../../shared/hooks/urlFilters";
import type {
  ReleaseSlotBookingView,
  ReleaseSlotName,
  SlotView,
} from "../../shared/types/api";
import { Button, Card, ErrorState, LoadingState, PageHeader, cn } from "../../shared/ui";
import { BookSlotDrawer, SlotDecisionDrawer } from "./ReleaseSlotDrawers";

/* Outside the component so its identity is stable — useUrlFilters memoises on it. */
const CALENDAR_FILTERS = { month: "" };

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** How many chips fit in a cell before the rest collapse into "+N more". */
const CHIPS_PER_CELL = 3;

/**
 * The release calendar: four places a day, and who has them (§44).
 *
 * <p>A month grid built the way people already know how to read one — a continuous ruled grid,
 * the date in the corner, today ringed, and each booking a chip. Nothing is drawn for an empty
 * day, because a calendar's empty squares are its most useful feature: they are where the room
 * is, and labelling them makes them harder to see rather than easier.
 *
 * <p>Deliberately not an hour-by-hour grid. A release is not an appointment that starts at 10:40
 * — it takes a morning or an afternoon, and the people it takes are busy for that long whatever
 * the runbook says. So the half-day lives on the chip, where a time would go.
 */
export function ReleaseCalendarPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useUrlFilters(CALENDAR_FILTERS);
  const month = filters.month === "" ? thisMonth() : filters.month;
  const [booking, setBooking] = useState<{
    date: string;
    morningLeft: number;
    afternoonLeft: number;
  } | null>(null);
  const [deciding, setDeciding] = useState<ReleaseSlotBookingView | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const [from, to] = useMemo(() => monthRange(month), [month]);

  const calendar = useQuery({
    queryKey: ["release-slots", from, to],
    queryFn: () => api.get<SlotView[]>(`/api/v1/release-slots?from=${from}&to=${to}`),
  });

  /*
   * What is waiting on this person specifically — not everything pending, which would show an
   * approver work they cannot act on. The chips on the grid remain clickable, but somebody who
   * opens this page to approve should not have to hunt the month for amber.
   */
  const mine = useQuery({
    queryKey: ["release-slots", "awaiting-me"],
    queryFn: () => api.get<ReleaseSlotBookingView[]>("/api/v1/release-slots/awaiting-me"),
    enabled: can("RELEASE_SLOT_APPROVE"),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["release-slots"] });
    void queryClient.invalidateQueries({ queryKey: ["release-slot-bookings"] });
  };

  /* Keyed by day so a cell is one lookup rather than a scan of the month. */
  const byDay = useMemo(() => {
    const map = new Map<string, { morning?: SlotView; afternoon?: SlotView }>();
    for (const slot of calendar.data ?? []) {
      const day = map.get(slot.date) ?? {};
      if (slot.slot === "MORNING") {
        day.morning = slot;
      } else {
        day.afternoon = slot;
      }
      map.set(slot.date, day);
    }
    return map;
  }, [calendar.data]);

  const days = useMemo(() => gridFor(month), [month]);

  /*
   * What the month looks like at a glance. Counted over the days actually in it rather than the
   * whole grid, which overruns into the months either side — a figure that silently included
   * next month's releases would be wrong in the way nobody checks.
   */
  const summary = useMemo(() => {
    const inMonth = new Set(days.filter((day) => day.inMonth).map((day) => day.iso));
    let approved = 0;
    let waiting = 0;
    let emergency = 0;
    const busy = new Set<string>();
    for (const slot of calendar.data ?? []) {
      if (!inMonth.has(slot.date)) {
        continue;
      }
      if (slot.bookings.length > 0) {
        busy.add(slot.date);
      }
      for (const booking of slot.bookings) {
        if (booking.emergency) {
          emergency += 1;
        } else if (booking.status === "APPROVED") {
          approved += 1;
        } else {
          waiting += 1;
        }
      }
    }
    return { approved, waiting, emergency, days: busy.size };
  }, [calendar.data, days]);

  return (
    <>
      <PageHeader
        title="Release calendar"
        subtitle="Four places a day — two in the morning, two in the afternoon. A release takes its slot on the day it is already planned for."
      />

      <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setFilters({ month: thisMonth() })}
        >
          Today
        </Button>
        <div className="flex items-center rounded-lg ring-1 ring-inset ring-ink-200">
          <Button
            variant="ghost"
            size="sm"
            icon={<ChevronLeft className="h-4 w-4" />}
            aria-label="Previous month"
            onClick={() => setFilters({ month: shiftMonth(month, -1) })}
          />
          <Button
            variant="ghost"
            size="sm"
            icon={<ChevronRight className="h-4 w-4" />}
            aria-label="Next month"
            onClick={() => setFilters({ month: shiftMonth(month, 1) })}
          />
        </div>
        <h2 className="font-display text-2xl font-semibold tracking-tight text-ink-900">
          {monthLabel(month)}
        </h2>

        {/* What the month holds, which is the question the grid takes a while to answer. */}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Tally tone="green" count={summary.approved} label="approved" />
          <Tally tone="amber" count={summary.waiting} label="waiting" />
          {summary.emergency > 0 && (
            <Tally tone="red" count={summary.emergency} label="emergency" />
          )}
          <span className="rounded-full bg-ink-100 px-2.5 py-1 text-xs font-medium text-ink-600">
            {summary.days} release {summary.days === 1 ? "day" : "days"}
          </span>
        </div>
      </div>

      {/* The approver's queue, above the grid: it is why they came. */}
      {(mine.data?.length ?? 0) > 0 && (
        <Card className="mb-4 border-amber-300 bg-amber-50/60">
          <div className="p-4">
            <h3 className="mb-0.5 font-display text-sm font-semibold text-ink-900">
              Waiting for you to decide
            </h3>
            <p className="mb-3 text-xs text-ink-600">
              {mine.data?.length} booking{mine.data?.length === 1 ? "" : "s"} on projects you
              answer for.
            </p>
            <ul className="space-y-2">
              {(mine.data ?? []).map((entry) => (
                <li
                  key={entry.id}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-surface p-2.5 ring-1 ring-inset ring-ink-200"
                >
                  {entry.emergency && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
                      <AlertTriangle className="h-3 w-3" /> Over the limit
                    </span>
                  )}
                  <span className="font-medium text-ink-900">{entry.projectName}</span>
                  <span className="text-sm text-ink-600">
                    {entry.releaseName}
                    {entry.version ? ` (${entry.version})` : ""}
                  </span>
                  <span className="text-sm text-ink-500">
                    {entry.slot === "MORNING" ? "Morning" : "Afternoon"} of{" "}
                    {longDay(entry.slotDate)}
                  </span>
                  <span className="text-xs text-ink-400">asked by {entry.requestedByName}</span>
                  <Button
                    size="sm"
                    className="ml-auto"
                    onClick={() => setDeciding(entry)}
                  >
                    Decide
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        </Card>
      )}

      {calendar.isPending ? (
        <Card>
          <div className="p-10">
            <LoadingState label="Reading the calendar" />
          </div>
        </Card>
      ) : calendar.isError ? (
        <ErrorState error={calendar.error} onRetry={() => void calendar.refetch()} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-ink-200 bg-surface shadow-sm">
          {/* The ruled grid people already know how to read: hairlines, no gaps. */}
          <div className="grid grid-cols-7 border-b border-ink-200 bg-ink-50">
            {DAY_NAMES.map((name, index) => (
              <div
                key={name}
                className={cn(
                  "px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide",
                  index >= 5 ? "text-ink-400" : "text-ink-500",
                )}
              >
                {name}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {days.map((day) => {
              const slots = byDay.get(day.iso);
              return (
                <DayCell
                  key={day.iso}
                  iso={day.iso}
                  inMonth={day.inMonth}
                  weekend={day.weekend}
                  morning={slots?.morning}
                  afternoon={slots?.afternoon}
                  expanded={expanded === day.iso}
                  canBook={can("RELEASE_SLOT_BOOK") && !isPast(day.iso)}
                  onExpand={() => setExpanded(expanded === day.iso ? null : day.iso)}
                  onBook={() =>
                    setBooking({
                      date: day.iso,
                      morningLeft: slots?.morning?.placesLeft ?? 2,
                      afternoonLeft: slots?.afternoon?.placesLeft ?? 2,
                    })
                  }
                  onOpen={(entry) =>
                    can("RELEASE_SLOT_APPROVE") && entry.status === "PENDING"
                      ? setDeciding(entry)
                      : navigate(`/releases/${entry.releaseId}`)
                  }
                />
              );
            })}
          </div>
        </div>
      )}

      <BookSlotDrawer
        open={booking != null}
        date={booking?.date ?? null}
        morningLeft={booking?.morningLeft ?? 2}
        afternoonLeft={booking?.afternoonLeft ?? 2}
        onClose={() => setBooking(null)}
        onSaved={refresh}
      />
      <SlotDecisionDrawer
        booking={deciding}
        onClose={() => setDeciding(null)}
        onSaved={() => {
          refresh();
          void queryClient.invalidateQueries({ queryKey: ["release-slots", "awaiting-me"] });
        }}
      />
    </>
  );
}

/** One figure from the month's summary, coloured the way its chips are. */
function Tally({
  tone,
  count,
  label,
}: {
  tone: "green" | "amber" | "red";
  count: number;
  label: string;
}) {
  const tones = {
    green: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
    amber: "bg-amber-50 text-amber-700 ring-amber-600/20",
    red: "bg-red-50 text-red-700 ring-red-600/20",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset",
        tones[tone],
      )}
    >
      <span className="tnum font-semibold">{count}</span>
      {label}
    </span>
  );
}

/**
 * One square of the grid.
 *
 * <p>An empty day draws nothing but its number. The "+" appears on hover, the way a calendar
 * lets you click empty space — a permanent button in every square would put forty of them on
 * screen competing with the handful of things actually happening.
 */
function DayCell({
  iso,
  inMonth,
  weekend,
  morning,
  afternoon,
  expanded,
  canBook,
  onExpand,
  onBook,
  onOpen,
}: {
  iso: string;
  inMonth: boolean;
  weekend: boolean;
  morning: SlotView | undefined;
  afternoon: SlotView | undefined;
  expanded: boolean;
  canBook: boolean;
  onExpand: () => void;
  onBook: () => void;
  onOpen: (booking: ReleaseSlotBookingView) => void;
}) {
  const today = isToday(iso);
  const past = isPast(iso);

  /* Morning first, then afternoon: the order the day happens in. */
  const entries = [
    ...(morning?.bookings ?? []).map((b) => ({
      booking: b,
      slot: "MORNING" as ReleaseSlotName,
    })),
    ...(afternoon?.bookings ?? []).map((b) => ({
      booking: b,
      slot: "AFTERNOON" as ReleaseSlotName,
    })),
  ];
  const left = (morning?.placesLeft ?? 2) + (afternoon?.placesLeft ?? 2);
  const shown = expanded ? entries : entries.slice(0, CHIPS_PER_CELL);
  const hidden = entries.length - shown.length;

  return (
    <div
      className={cn(
        "group relative min-h-32 border-b border-r border-ink-200 p-2 transition-colors",
        !inMonth && "bg-ink-50/70",
        weekend && inMonth && "bg-ink-50/40",
        past && inMonth && "bg-ink-50/50",
        /* Today is the one square people look for first. */
        today && "bg-brand-50/50",
      )}
    >
      <div className="mb-1.5 flex items-start justify-between">
        <span
          className={cn(
            "grid h-7 min-w-7 place-items-center rounded-full px-1 text-[13px] tabular-nums",
            today && "bg-brand-600 font-semibold text-white shadow-sm",
            !today && inMonth && "font-medium text-ink-800",
            !today && !inMonth && "text-ink-400",
          )}
        >
          {Number(iso.slice(8, 10))}
        </span>

        <div className="flex items-center gap-1">
          {/* The day's four places, drawn. Filled is taken, hollow is free — the capacity rule
              made visible, which a number never quite is. */}
          {inMonth && !past && (
            <Places
              morningLeft={morning?.placesLeft ?? 2}
              afternoonLeft={afternoon?.placesLeft ?? 2}
              quiet={entries.length === 0}
            />
          )}

          {/* Click empty space to book, the way a calendar works. Hidden until hover. */}
          {canBook && inMonth && left > 0 && (
            <button
              type="button"
              onClick={onBook}
              aria-label={`Book a slot on ${iso}`}
              title="Book a slot"
              className="grid h-6 w-6 place-items-center rounded-full text-ink-400 opacity-0 transition hover:bg-brand-100 hover:text-brand-700 focus:opacity-100 group-hover:opacity-100"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="space-y-0.5">
        {shown.map(({ booking, slot }) => (
          <BookingChip key={booking.id} booking={booking} slot={slot} onOpen={onOpen} />
        ))}

        {hidden > 0 && (
          <button
            type="button"
            onClick={onExpand}
            className="w-full rounded px-1.5 py-px text-left text-[11px] font-medium text-ink-500 hover:bg-ink-100 hover:text-ink-900"
          >
            +{hidden} more
          </button>
        )}
        {expanded && entries.length > CHIPS_PER_CELL && (
          <button
            type="button"
            onClick={onExpand}
            className="w-full rounded px-1.5 py-px text-left text-[11px] font-medium text-ink-500 hover:bg-ink-100 hover:text-ink-900"
          >
            Show less
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * The day's four places: two for the morning, two for the afternoon, with a gap between the
 * pairs so the halves read apart.
 *
 * <p>Drawn rather than counted. "2 free" on forty squares is forty pieces of text to read;
 * four dots is a shape, and a full day looks different from an empty one across the room.
 */
function Places({
  morningLeft,
  afternoonLeft,
  quiet,
}: {
  morningLeft: number;
  afternoonLeft: number;
  /** An untouched day keeps them hidden until the cursor is on it. */
  quiet: boolean;
}) {
  const pip = (taken: boolean, key: string) => (
    <span
      key={key}
      className={cn(
        "h-1.5 w-1.5 rounded-full",
        taken ? "bg-ink-400" : "bg-ink-200 ring-1 ring-inset ring-ink-300/60",
      )}
    />
  );
  return (
    <span
      className={cn(
        "flex items-center gap-[3px] transition-opacity group-hover:opacity-100",
        quiet ? "opacity-0" : "opacity-70",
      )}
      title={`Morning: ${morningLeft} free · Afternoon: ${afternoonLeft} free`}
    >
      {pip(morningLeft < 2, "am1")}
      {pip(morningLeft < 1, "am2")}
      <span className="mx-px h-2 w-px bg-ink-200" aria-hidden />
      {pip(afternoonLeft < 2, "pm1")}
      {pip(afternoonLeft < 1, "pm2")}
    </span>
  );
}

/** One booking: the half-day where a time would go, and what is going out. */
function BookingChip({
  booking,
  slot,
  onOpen,
}: {
  booking: ReleaseSlotBookingView;
  slot: ReleaseSlotName;
  onOpen: (booking: ReleaseSlotBookingView) => void;
}) {
  /* An emergency is not a third status — it is an approved or waiting booking that went over
     the day's limit — but it is the one somebody must not skim past, so it takes the colour. */
  /* The 700 step, not 900: the dark theme re-maps 50–700 of each scale and leaves 900 alone,
     so a 900 text sits dark on a dark tint and the chip becomes unreadable at night. */
  const look = booking.emergency
    ? "border-red-500 bg-red-50 text-red-700 ring-red-600/20 hover:bg-red-100"
    : booking.status === "APPROVED"
      ? "border-emerald-500 bg-emerald-50 text-emerald-700 ring-emerald-600/15 hover:bg-emerald-100"
      : "border-amber-400 bg-amber-50 text-amber-700 ring-amber-600/15 hover:bg-amber-100";

  return (
    <button
      type="button"
      onClick={() => onOpen(booking)}
      title={`${booking.projectName} — ${booking.releaseName}${
        booking.emergency ? " (emergency)" : ""
      }`}
      className={cn(
        "flex w-full items-center gap-1.5 overflow-hidden rounded-md border-l-[3px] py-1 pl-1.5 pr-1",
        "text-left text-[11px] leading-4 ring-1 ring-inset transition",
        look,
      )}
    >
      {booking.emergency && (
        <AlertTriangle className="h-3 w-3 shrink-0" aria-label="Emergency" />
      )}
      <span className="shrink-0 rounded bg-black/5 px-1 text-[9px] font-bold uppercase tracking-wide dark:bg-white/10">
        {slot === "MORNING" ? "AM" : "PM"}
      </span>
      <span className="truncate font-semibold">{booking.projectKey}</span>
      <span className="truncate opacity-75">{booking.version ?? booking.releaseName}</span>
    </button>
  );
}

// ── dates ─────────────────────────────────────────────────────────────────────────────────

/* Kept as YYYY-MM-DD strings rather than Date objects: the month is in the URL, and a string
   that is already the thing the URL carries cannot drift from it by a timezone. */

/** Whether a day has gone. Today has not: a release going out this afternoon is ordinary. */
function isPast(iso: string): boolean {
  return iso < todayIso();
}

function isToday(iso: string): boolean {
  return iso === todayIso();
}

/* The local day, not the UTC one: somebody in Kolkata at 02:00 is on today, not yesterday. */
function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

function thisMonth(): string {
  return todayIso().slice(0, 7);
}

/** The year and zero-based month of a YYYY-MM string. */
function partsOf(month: string): { year: number; monthIndex: number } {
  const [year, m] = month.split("-");
  return { year: Number(year), monthIndex: Number(m) - 1 };
}

function shiftMonth(month: string, by: number): string {
  const { year, monthIndex } = partsOf(month);
  return new Date(Date.UTC(year, monthIndex + by, 1)).toISOString().slice(0, 7);
}

/** "Wednesday, 7 October" — long enough to read without hunting the grid for it. */
function longDay(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}

function monthLabel(month: string): string {
  const { year, monthIndex } = partsOf(month);
  return new Date(Date.UTC(year, monthIndex, 1)).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** The first and last day the grid shows, which overruns the month at both ends. */
function monthRange(month: string): [string, string] {
  const days = gridFor(month);
  return [days.at(0)?.iso ?? `${month}-01`, days.at(-1)?.iso ?? `${month}-28`];
}

/**
 * Six weeks of days, Monday first, covering the whole month.
 *
 * <p>Always six rows rather than however many the month needs, so the grid does not change
 * height as somebody pages through it — a calendar that jumps is one people lose their place in.
 */
function gridFor(month: string): { iso: string; inMonth: boolean; weekend: boolean }[] {
  const { year, monthIndex } = partsOf(month);
  const first = new Date(Date.UTC(year, monthIndex, 1));
  /* getUTCDay is 0 for Sunday; the grid starts on Monday. */
  const lead = (first.getUTCDay() + 6) % 7;
  const start = new Date(Date.UTC(year, monthIndex, 1 - lead));

  const days: { iso: string; inMonth: boolean; weekend: boolean }[] = [];
  for (let i = 0; i < 42; i += 1) {
    const day = new Date(start);
    day.setUTCDate(start.getUTCDate() + i);
    const weekday = day.getUTCDay();
    days.push({
      iso: day.toISOString().slice(0, 10),
      inMonth: day.getUTCMonth() === monthIndex,
      weekend: weekday === 0 || weekday === 6,
    });
  }
  return days;
}
